import React, { useEffect } from 'react';
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FormControlLabel, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Switch } from '@mui/material';
dayjs.extend(customParseFormat);

const TextFieldWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth>
            <TextField
                disabled={config.disabled}
                variant="outlined"
                required={config.required}
                id={config.name}
                label={config.title}
                name={config.name}
                value={values[config.name] || ''}
                onChange={handleChange}
                error={Boolean(errors[config.name] && touched[config.name])}
            />
        </FormControl>
    );
};

const PasswordWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth>
            <TextField
                type="password"
                variant="outlined"
                required={config.required}
                id={config.name}
                label={config.title}
                name={config.name}
                value={values[config.name] || ''}
                onChange={handleChange}
                error={Boolean(errors[config.name] && touched[config.name])}
            />
        </FormControl>
    );
};

const TextAreaWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth>
            <TextField
                variant="outlined"
                required={config.required}
                id={config.name}
                label={config.title}
                name={config.name}
                value={values[config.name] || ''}
                onChange={handleChange}
                error={Boolean(errors[config.name] && touched[config.name])}
                multiline
                rows={4}
            />
        </FormControl>
    );
};

const NumberWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth>
            <TextField
                type="number"
                variant="outlined"
                disabled={config.disabled}
                required={config.required}
                id={config.name}
                label={config.title}
                name={config.name}
                value={values[config.name] || ''}
                onChange={handleChange}
                error={Boolean(errors[config.name] && touched[config.name])}
            />
        </FormControl>
    );
};

const DecimalWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth>
            <TextField
                disabled={config.disabled}
                type="number"
                step="0.00"
                variant="outlined"
                required={config.required}
                id={config.name}
                label={config.title}
                name={config.name}
                value={values[config.name] || ''}
                onChange={handleChange}
                error={Boolean(errors[config.name] && touched[config.name])}
            />
        </FormControl>
    );
};

const DatePickerWrapper = ({ config, values, handleChange, errors, touched }) => {
    const date = values[config.name] === null ? null : dayjs(values[config.name], 'YYYY-MM-DD');
    const handleDateChange = (date) => {
        const adjustedDate = dayjs(date).format('YYYY-MM-DD');
        handleChange({ target: { name: config.name, value: adjustedDate } });
    };
    return (
        <FormControl fullWidth style={{ paddingTop: "0px" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                {/* <DemoContainer components={['DatePicker']}> */}
                <DatePicker
                    views={['year', 'month', 'day']}
                    slotProps={{
                        textField: {
                            required: config.required,
                            error: Boolean(errors[config.name])
                        },
                    }}
                    disabled={config.disabled}
                    // inputFormat="dd/MM/yyyy"
                    required={config.required}
                    id={config.name}
                    label={config.title}
                    name={config.name}
                    value={date}
                    onChange={handleDateChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                        />
                    )}
                    format="DD-MMM-YYYY"
                />
                {/* </DemoContainer> */}
            </LocalizationProvider>
        </FormControl>
    );
};

const SelectWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth required={config.required} error={Boolean(errors[config.name] && touched[config.name])}>
            <InputLabel id={`${config.name}-label`}>{config.title}</InputLabel>
            <Select
                disabled={config.disabled}
                labelId={`${config.name}-label`}
                id={config.name}
                name={config.name}
                value={values[config.name] || ''}
                label={config.title}
                onChange={handleChange}
            >
                {config.options.map((item, index) => (
                    <MenuItem key={index} value={config.itemValue === "fullItem" ? item : item[config.itemValue]}>{item[config.displayKey]}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const MultiSelectWrapper = ({ config, values, handleChange, errors, touched }) => {
    const [value, setValue] = React.useState([]);

    const updateValues = (value) => {
        setValue(value)
        values[config.name] = value.map((item) => item[config.itemValue]);
    };

    useEffect(() => {
        if (values[config.name] !== undefined && values[config.name] !== null && values[config.name].length !== 0) {
            setValue(config.options.filter((option) => values[config.name].indexOf(option[config.itemValue]) > -1))
        }
    }, [config.options, values[config.name]]);

    return (
        <FormControl fullWidth error={Boolean(errors[config.name] && touched[config.name])}>
            <Autocomplete
                multiple
                // labelId={`${config.name}-label`}
                id="tags-outlined"
                name={config.name}
                value={value}
                options={config.options}
                getOptionLabel={(option) => option[config.displayKey]}
                filterSelectedOptions
                // getOptionSelected={(option, value) => option[config.itemValue] === value[config.itemValue]}
                onChange={(event, newValue) => {
                    updateValues(newValue);
                }}
                renderInput={(params) => (
                    <TextField
                        error={Boolean(errors[config.name] && touched[config.name])}
                        required={config.required}
                        {...params}
                        label={config.title}
                    />
                )}
            />
            {/* <InputLabel id={`${config.name}-label`}>{config.title}</InputLabel>
            <Select
                multiple
                required={config.required}
                labelId={`${config.name}-label`}
                id={config.name}
                name={config.name}
                value={values[config.name] || ''}
                label={config.title}
                onChange={handleChange}
            >
                {config.options.map((item, index) => (
                    <MenuItem key={index} value={config.itemValue === "fullItem" ? item : item[config.itemValue]}>{item[config.displayKey]}</MenuItem>
                ))}
            </Select> */}
        </FormControl>
    );
};

const SwitchWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }} >
            <FormLabel component="legend">{config.title}&nbsp;&nbsp;&nbsp;</FormLabel>
            <FormControlLabel
                control={<Switch id={config.name} name={config.name} value={values[config.name]} checked={values[config.name]} onChange={handleChange} />}
                label={config.options.find((option) => option.id === values[config.name]).title} />
        </FormControl>
    );
};

const AutoCompleteWrapper = ({ config, values, handleChange, errors, touched }) => {
    const [value, setValue] = React.useState(null);

    useEffect(() => {
        if (config.options.length > 0 && values[config.name] !== null) {
            setValue(config.options.find((option) => option[config.itemValue] === values[config.name]))
        }
    }, [config.options, values[config.name]]);

    return (
        <FormControl fullWidth required={config.required} error={Boolean(errors[config.name] && touched[config.name])}>
            <Autocomplete
                disablePortal
                // labelId={`${config.name}-label`}
                id={config.name}
                name={config.name}
                value={value}
                options={config.options}
                getOptionLabel={(option) => String(option[config.displayKey])}
                getOptionKey={(option) => option[config.itemValue]}
                // getOptionSelected={(option, value) => option[config.itemValue] === value[config.itemValue]}
                onChange={(event, newValue) => {
                    handleChange({
                        target: {
                            name: config.name,
                            value: newValue !== null ? newValue[config.itemValue] : null
                        }
                    });
                    setValue(newValue);
                    values[config.name] = newValue !== null ? newValue[config.itemValue] : null;
                }}

                renderInput={(params) => <TextField
                    error={Boolean(errors[config.name] && touched[config.name])}
                    required={config.required}
                    {...params}
                    label={config.title} />}
            />
        </FormControl>
    )
};

const RadioWrapper = ({ config, values, handleChange, errors, touched }) => {
    return (
        <FormControl fullWidth>
            <FormLabel id="demo-row-radio-buttons-group-label">{config.title}</FormLabel>
            <RadioGroup
                row
                variant="outlined"
                required={config.required}
                id={config.name}
                label={config.title}
                name={config.name}
                value={values[config.name]}
                onChange={handleChange}
                error={Boolean(errors[config.name] && touched[config.name])}
            >
                {config.options.map((item, index) => (
                    <FormControlLabel key={index} value={config.itemValue === "fullItem" ? item : item[config.itemValue]} label={item[config.displayKey]} control={<Radio />} />
                ))}
            </RadioGroup>
        </FormControl>
    );
};

const GoForm = (props) => {
    const { config } = props;
    switch (config.type) {
        case 'text':
            return <TextFieldWrapper {...props} />;
        case 'password':
            return <PasswordWrapper {...props} />;
        case 'textarea':
            return <TextAreaWrapper {...props} />;
        case 'number':
            return <NumberWrapper {...props} />;
        case 'deciaml':
            return <DecimalWrapper {...props} />;
        case 'autocomplete':
            return <AutoCompleteWrapper {...props} />;
        case 'datepicker':
            return <DatePickerWrapper {...props} />;
        case 'select':
            return <SelectWrapper {...props} />;
        case 'multiselect':
            return <MultiSelectWrapper {...props} />;
        case 'switch':
            return <SwitchWrapper {...props} />;
        case 'radio':
            return <RadioWrapper {...props} />;
        default:
            return null;
    }
};

export default GoForm;
